import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { EFP_COVER_CATEGORIES } from '@om/redux/actions/easiplusFuneralPlan/types';
import {
    setAgeValue,
    setCoverAmountValue,
    setCoverMemberValue,
    setShowingAgeValidation,
} from '@om/redux/actions/easiplusFuneralPlan/quote';
import {
    getAge,
    getAgeHintText,
    getAgePlaceholderText,
    getAllExtendedFamilyCoverOptions,
    getCoverAmount,
    getCoverAmountLimits,
    getDisplayCoverAmount,
    getDisplayMonthlyPremium,
    getExtendedFamilyDrawerIsOpen,
    getRelationship,
    getShowingAgeValidation,
    getSliderSteps,
} from '@om/redux/selectors/easiplusFuneralPlan/quote';
import { OmFormDropdownFieldWrapper, OmFormInputFieldWrapper, OmSlider, OmLoader } from '@om/component-library-react';
import QuoteSideDrawerWrapper from './QuoteSideDrawerWrapper';
import { DEFAULT_VALIDATION_DELAY } from '../../../config';
import {
    getIsQuoteReferenceDataValid,
    getIsApplicationReferenceDataValid,
} from '@om/redux/selectors/easiplusFuneralPlan/referenceData';

const FormContent = () => {
    const {
        extendedDrawerOpen,
        allExtendedFamilyCoverOptions,
        coverAmount,
        displayCoverAmount,
        displayMonthlyPremium,
        relationship,
        age,
        coverAmountLimits,
        ageHintText,
        agePlaceholderText,
        showingAgeValidation,
        sliderSteps,
    } = useSelector(
        createStructuredSelector({
            extendedDrawerOpen: getExtendedFamilyDrawerIsOpen,
            allExtendedFamilyCoverOptions: getAllExtendedFamilyCoverOptions,
            coverAmount: getCoverAmount,
            displayCoverAmount: getDisplayCoverAmount,
            displayMonthlyPremium: getDisplayMonthlyPremium,
            relationship: getRelationship,
            age: getAge,
            coverAmountLimits: getCoverAmountLimits,
            ageHintText: getAgeHintText,
            agePlaceholderText: getAgePlaceholderText,
            showingAgeValidation: getShowingAgeValidation,
            sliderSteps: getSliderSteps,
        })
    );

    const dispatch = useDispatch();
    return (
        <>
            <QuoteSideDrawerWrapper
                drawerIsOpen={extendedDrawerOpen}
                editCategory={EFP_COVER_CATEGORIES.EXTENDED_FAMILY}
            >
                <h6 className="no-top-margin medium">My Extended Family</h6>
                <OmFormDropdownFieldWrapper
                    useNativeMobileDropdown
                    required
                    align="left"
                    label=" "
                    placeholderAsLabel={false}
                    placeholder="What's your relationship?"
                    formId="addExtendedFamily"
                    size="full"
                    name="Relationship"
                    value={relationship && relationship.value}
                    state={relationship && relationship.status}
                    errorMessage={relationship && relationship.error}
                    options={JSON.stringify(
                        allExtendedFamilyCoverOptions &&
                            allExtendedFamilyCoverOptions.map((coverCategory) => ({
                                value: coverCategory,
                                label: coverCategory,
                            }))
                    )}
                    onOnSelect={(event) => dispatch(setCoverMemberValue(event.detail.value))}
                />

                {relationship && relationship.value && (
                    <OmFormInputFieldWrapper
                        id="ageField"
                        mask="number"
                        size="full"
                        align="left"
                        label=" "
                        required
                        keyboardMode="numeric"
                        placeholder={agePlaceholderText}
                        hintText={ageHintText}
                        validationDelay={DEFAULT_VALIDATION_DELAY}
                        value={age && age.value}
                        state={age && age.status}
                        errorMessage={age && age.error}
                        onShowingVlidation={(event) => dispatch(setShowingAgeValidation(event.detail))}
                        onPersist={(event) => dispatch(setAgeValue(event.detail))}
                    />
                )}

                <div>
                    {age && age.status === 'valid' && showingAgeValidation && (
                        <>
                            <h6 className="no-bottom-margin">Select cover amount:</h6>
                            <OmSlider
                                stepString={JSON.stringify(sliderSteps)}
                                minLabel={coverAmountLimits.minLabel}
                                maxLabel={coverAmountLimits.maxLabel}
                                value={coverAmount.value}
                                onSliderChange={(event) => dispatch(setCoverAmountValue(event.detail))}
                            />
                            <div className="cover-label-wrapper">
                                <span className="cover-label">
                                    <h5 className="no-bottom-margin no-top-margin medium">{displayCoverAmount}</h5>
                                    <h6 className="small medium no-top-margin">COVER AMOUNT</h6>
                                </span>

                                <span className="premium-label">
                                    <h5 className="no-bottom-margin no-top-margin medium">{displayMonthlyPremium}</h5>
                                    <h6 className="small medium no-top-margin">TOTAL MONTHLY PREMIUM</h6>
                                </span>
                            </div>
                        </>
                    )}
                </div>
            </QuoteSideDrawerWrapper>
        </>
    );
};

const loadingText = (extendedDrawerOpen) => {
    return (
        <>
            <QuoteSideDrawerWrapper drawerIsOpen={extendedDrawerOpen} editCategory={EFP_COVER_CATEGORIES.DIRECT_FAMILY}>
                <OmLoader loaderText={'Loading...'} isLoading={true} />
            </QuoteSideDrawerWrapper>
        </>
    );
};

const ExtendedFamilyDrawer = () => {
    const { extendedDrawerOpen, quoteReferenceDataValid, applicationReferenceDataValid } = useSelector(
        createStructuredSelector({
            extendedDrawerOpen: getExtendedFamilyDrawerIsOpen,
            quoteReferenceDataValid: getIsQuoteReferenceDataValid,
            applicationReferenceDataValid: getIsApplicationReferenceDataValid,
        })
    );

    if (applicationReferenceDataValid && quoteReferenceDataValid) {
        return <FormContent />;
    }

    return <div>{loadingText(extendedDrawerOpen)}</div>;
};

export default ExtendedFamilyDrawer;
